import {
  BaseRepository,
  IBaseModel,
  IListFetchParams,
  MN_RESOURCES,
  MN_TIMESHEET_LOCK,
  MODULE,
} from '../common';
import type { Prisma } from '@prisma/client';
import * as z from 'zod';
import { checkIfAdmin } from '@jobcost-monorepo/utils';
import type { KeyValue } from '@jobcost-monorepo/utils';

type TimesheetLock = Prisma.$TimesheetLockPayload['scalars'];

export type ITimesheetLock = TimesheetLock &
  Omit<IBaseModel, 'createdAt' | 'updatedAt' | 'id'> & {
    resource: KeyValue;
  };

export interface IListTimesheetLockFetchParams extends IListFetchParams {
  resourceId?: number;
  startDate?: string;
  endDate?: string;
}

class TimesheetLockRepository extends BaseRepository<
  ITimesheetLock,
  IListTimesheetLockFetchParams
> {
  constructor(moduleName: MODULE) {
    super(moduleName);
    this.keyValueMapFields = {
      idlinkedField: 'id',
      descriptionlinkedField: ['resourceId'],
    };
    this.gridFieldsOptions = [
      {
        visible: true,
        position: 0,
        linkedField: 'id',
        type: '',
        align: 'left',
        width: '12rem',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 1,
        linkedField: 'resource.name',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'string',
      },
      {
        visible: true,
        position: 2,
        linkedField: 'lockDate',
        type: '',
        align: 'left',
        width: 'w-full',
        sortable: false,
        formatType: 'date',
      },
    ];

    this.gridFiltersOptions = [
      {
        position: { x: 0, y: 0 },
        linkedField: 'resourceId',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_RESOURCES,
      },
      {
        position: { x: 0, y: 4 },
        linkedField: 'startDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_TIMESHEET_LOCK,
        translationModuleName: MN_TIMESHEET_LOCK,
      },
      {
        position: { x: 1, y: 4 },
        linkedField: 'endDate',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_TIMESHEET_LOCK,
        translationModuleName: MN_TIMESHEET_LOCK,
      },
    ];

    this.formFieldsOptions = [
      {
        visible: false,
        readOnly: true,
        position: { x: 0, y: 0 },
        linkedField: 'id',
        type: 'string',
        width: '12rem',
        formatType: 'input',
        moduleName: MN_TIMESHEET_LOCK,
        translationModuleName: MN_TIMESHEET_LOCK,
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 0 },
        linkedField: 'resourceId',
        linkedFieldForDefaultValue: 'resource.name',
        type: 'number',
        width: 'w-full',
        formatType: 'dropdownWithSearch',
        moduleName: MN_RESOURCES,
        translationModuleName: MN_TIMESHEET_LOCK,
        checkIfDisabled: () => !checkIfAdmin(),
      },
      {
        visible: true,
        readOnly: false,
        position: { x: 0, y: 2 },
        linkedField: 'lockDate',
        type: 'date',
        width: 'w-full',
        formatType: 'date',
        moduleName: MN_TIMESHEET_LOCK,
        translationModuleName: MN_TIMESHEET_LOCK,
      },
    ];

    // @ts-ignore
    this.formValidationSchema = z.object({
      id: z.number().nullable().optional(),
      resourceId: z.number({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
      lockDate: z.string({
        invalid_type_error: 'fieldRequired',
        required_error: 'fieldRequired',
      }),
    });
  }
}

export default TimesheetLockRepository;
